import api, { ValidateAxiosError } from '@/modules/api.service';
import axios from 'axios';
import { API_URL } from '@/shared/constants/strings';
import { CreatePersonDto, ReadPersonDto, ReadPersonSelectDto, UpdatePersonDto, ReadPersonFilesDto, EventoTimeline } from '@/api.types';
import { Endpoints } from '@/shared/constants/endpoints';
import { FindPersonFilesFilterDto, FindPersonFiltersDto } from '@/api.types.extra';
import { getLoginTokenFromStorage } from '../system/auth/application/auth.service';

const endpoint = Endpoints.PESSOA;

export async function createPerson(dto: CreatePersonDto): Promise<string> {
  try {
    const response = await api.post(endpoint, dto);
    return response.data;
  } catch (error) {
    throw ValidateAxiosError(error, 'Falha ao cadastrar a pessoa.');
  }
}

export async function readPerson(filters: FindPersonFiltersDto): Promise<[ReadPersonDto[], number]> {
  if (filters) {
    filters.skip ??= 0;
    filters.take ??= 10;
  }

  try {
    const response = await api.get(endpoint, { params: filters });
    return response.data;
  } catch (error) {
    throw ValidateAxiosError(error, 'Falha ao buscar pessoa(s).');
  }
}

export async function readForSelect(filters?: FindPersonFiltersDto): Promise<[ReadPersonSelectDto[], number]> {
  try {
    const response = await api.get<[ReadPersonSelectDto[], number]>(`${endpoint}/select/all`, { params: filters });
    return response.data;
  } catch (error) {
    throw ValidateAxiosError(error, 'Falha ao buscar pessoa(s) para select.');
  }
}

export async function readPersonResponsibleSelect() {
  try {
    const response = await api.get<[ReadPersonSelectDto[], number]>(`${endpoint}/select`);
    return response.data;
  } catch (error) {
    throw ValidateAxiosError(error, 'Falha ao buscar responsáveis(s).');
  }
}

export async function updatePerson(dto: UpdatePersonDto): Promise<string> {
  try {
    const response = await api.put(endpoint, dto);
    return response.data;
  } catch (error) {
    throw ValidateAxiosError(error, 'Falha ao atualizar a pessoa.');
  }
}

export async function deletePerson(id: string): Promise<number> {
  try {
    const response = await api.delete(`${endpoint}/${id}`);
    return response.data;
  } catch (error) {
    throw ValidateAxiosError(error, 'Falha ao remover a pessoa.');
  }
}

// Anexos
export async function attachPersonFile(id: string, formData: FormData) {
  const api2 = axios.create({ baseURL: API_URL });
  api2.interceptors.request.use((config) => {
    // Não definir Content-Type para FormData; o axios define multipart/form-data com o boundary correto
    config.headers.Authorization = `Bearer ${getLoginTokenFromStorage()}`;
    return config;
  });

  await api2
    .post(endpoint + '/' + id + '/file', formData)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      console.warn(error);
      return ValidateAxiosError(error, 'Falha ao enviar arquivo.');
    });
}

export async function readPersonFile(filters: FindPersonFilesFilterDto) {
  try {
    const response = await api.get<[ReadPersonFilesDto[], number]>(`${endpoint}/files`, { params: filters });
    return response.data;
  } catch (error) {
    throw ValidateAxiosError(error, 'Falha ao buscar arquivo(s).');
  }
}

export async function deletePersonFile(id: string) {
  try {
    const response = await api.delete(`${endpoint}/file/${id}`);
    return response.data;
  } catch (error) {
    throw ValidateAxiosError(error, 'Falha ao remover o anexo.');
  }
}

function parseFilenameFromContentDisposition(disposition: string | undefined): string | undefined {
  if (!disposition) return undefined;
  const utf8 = /filename\*=UTF-8''([^;\n]+)/i.exec(disposition);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch {
      /* ignore */
    }
  }
  const plain = /filename="([^"]+)"/i.exec(disposition) ?? /filename=([^;\n]+)/i.exec(disposition);
  return plain?.[1]?.trim();
}

export async function downloadPersonFile(
  id: string,
  knownFilename?: string,
): Promise<{ arquivo: ArrayBuffer; filename: string }> {
  try {
    const response = await api.get(`${endpoint}/file/${id}`, { responseType: 'blob' });
    const blob = response.data as Blob;
    const arquivo = await blob.arrayBuffer();

    const fromHeader = parseFilenameFromContentDisposition(
      response.headers['content-disposition'] as string | undefined,
    );
    const filename = knownFilename?.trim() || fromHeader || 'arquivo';

    return { arquivo, filename };
  } catch (error) {
    throw ValidateAxiosError(error, 'Falha ao baixar o arquivo.');
  }
}

// Outras funções.

/**
 * Linha do tempo com o histórico de lotações da pessoa..
 * @param {number} id
 * @returns Linha do tempo da pessoa.
 */
export async function uploadPersonFoto(id: string, formData: FormData) {
  const api2 = axios.create({ baseURL: API_URL });
  api2.interceptors.request.use((config) => {
    // Não definir Content-Type para FormData; o axios define multipart/form-data com o boundary correto
    config.headers.Authorization = `Bearer ${getLoginTokenFromStorage()}`;
    return config;
  });

  try {
    const response = await api2.post(`${endpoint}/${id}/foto`, formData);
    return response.data;
  } catch (error) {
    throw ValidateAxiosError(error, 'Falha ao enviar foto.');
  }
}

export async function deletePersonFoto(id: string) {
  try {
    const response = await api.delete(`${endpoint}/${id}/foto`);
    return response.data;
  } catch (error) {
    throw ValidateAxiosError(error, 'Falha ao remover a foto.');
  }
}

export async function getPersonFotoUrl(id: string): Promise<string> {
  try {
    // Retorna a URL completa da foto
    return `${API_URL}/${endpoint}/${id}/foto`;
  } catch (error) {
    throw ValidateAxiosError(error, 'Falha ao obter URL da foto.');
  }
}

export async function fetchPersonFoto(id: string): Promise<Blob> {
  try {
    const api2 = axios.create({ baseURL: API_URL, responseType: 'blob' });
    api2.interceptors.request.use((config) => {
      config.headers.Authorization = `Bearer ${getLoginTokenFromStorage()}`;
      return config;
    });

    const response = await api2.get(`${endpoint}/${id}/foto`);
    return response.data;
  } catch (error) {
    throw ValidateAxiosError(error, 'Falha ao buscar foto.');
  }
}

export async function readTimeline(id: number) {
  return api
    .get<EventoTimeline[]>(Endpoints.DEFENSOR + '/timeline/' + id)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return ValidateAxiosError<EventoTimeline[]>(
        error,
        'Falha ao buscar linha do tempo.',
      );
    });
}